import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fmtNum, fmtPct } from '../../lib/formatHelpers';

/**
 * V2 PerformanceAnalytics — Bloc analytics réutilisable (admin + manager).
 *
 * Calcule depuis le roster `creators` (cleartext) :
 *  - KPIs : total, actifs, taux d'activité
 *  - Distribution par tier (tier_current ?? tier)
 *  - Breakdown par serveur (total / actifs / jours valides cumulés)
 *  - Top créateurs par valid_days
 *
 * Pas de financial $$ (R2 — agrégats cleartext uniquement).
 *
 * Props :
 *  - rows         : array<{ id, display_name, tiktok_username, server, tier_current, tier, status, valid_days }>
 *  - loading
 *  - creatorBasePath : '/admin/v2/creators' or '/manager/team'
 */
const Tile = ({ label, value, sub }) => (
  <div className="rounded-xl bg-card border border-border p-4">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 font-semibold">{label}</div>
    <div className="text-2xl md:text-3xl font-heading font-bold text-foreground tabular-nums mt-1">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const Bar = ({ label, value, total, accent }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-foreground font-medium">{label}</span>
        <span className="text-muted-foreground tabular-nums">{fmtNum(value)} ({fmtPct(pct, 0)})</span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${accent || 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const PerformanceAnalytics = ({ rows = [], loading = false, creatorBasePath = '/admin/v2/creators' }) => {
  const { t } = useTranslation();

  const stats = useMemo(() => {
    const total = rows.length;
    let active = 0;
    const tierMap = new Map();
    const serverMap = new Map();
    for (const c of rows) {
      if (c.status === 'active') active += 1;
      const tier = c.tier_current ?? c.tier ?? '—';
      tierMap.set(tier, (tierMap.get(tier) || 0) + 1);
      const srv = c.server || 'unknown';
      const s = serverMap.get(srv) || { total: 0, active: 0, validDays: 0 };
      s.total += 1;
      if (c.status === 'active') s.active += 1;
      s.validDays += c.valid_days || 0;
      serverMap.set(srv, s);
    }
    const tiers = Array.from(tierMap.entries()).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    const servers = Array.from(serverMap.entries()).sort((a, b) => b[1].total - a[1].total);
    const topCreators = [...rows].sort((a, b) => (b.valid_days || 0) - (a.valid_days || 0)).slice(0, 10);
    return { total, active, activeRate: total > 0 ? (active / total) * 100 : 0, tiers, servers, topCreators };
  }, [rows]);

  if (loading) {
    return <div className="rounded-2xl bg-card border border-border h-64 skeleton" />;
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Tile label={t('perfV2.totalCreators', 'Total creators')} value={fmtNum(stats.total)} />
        <Tile label={t('perfV2.activeCreators', 'Active creators')} value={fmtNum(stats.active)} sub={fmtPct(stats.activeRate, 0)} />
        <Tile label={t('perfV2.servers', 'Servers')} value={fmtNum(stats.servers.length)} />
        <Tile label={t('perfV2.tiers', 'Tiers')} value={fmtNum(stats.tiers.length)} />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Tier distribution */}
        <section className="rounded-2xl bg-card border border-border p-5">
          <div className="font-heading font-semibold text-foreground text-sm mb-4">
            {t('perfV2.tierDistribution', 'Tier distribution')}
          </div>
          {stats.tiers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('perfV2.noData', 'No data yet.')}</p>
          ) : (
            <div className="space-y-3">
              {stats.tiers.map(([tier, count]) => (
                <Bar key={String(tier)} label={`T${tier}`} value={count} total={stats.total} />
              ))}
            </div>
          )}
        </section>

        {/* Server breakdown */}
        <section className="rounded-2xl bg-card border border-border p-5">
          <div className="font-heading font-semibold text-foreground text-sm mb-4">
            {t('perfV2.serverBreakdown', 'Server breakdown')}
          </div>
          {stats.servers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">{t('perfV2.noData', 'No data yet.')}</p>
          ) : (
            <div className="space-y-3">
              {stats.servers.map(([srv, s]) => (
                <Bar key={srv} label={srv} value={s.active} total={s.total} accent="bg-success" />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Top creators by valid days */}
      <section className="rounded-2xl bg-card border border-border overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-surface-2 font-heading font-semibold text-foreground text-sm">
          {t('perfV2.topCreators', 'Top creators (valid days)')}
        </div>
        {stats.topCreators.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t('perfV2.noData', 'No data yet.')}</p>
        ) : (
          <ol className="divide-y divide-border">
            {stats.topCreators.map((c, i) => (
              <li key={c.id} className="px-5 py-2.5 flex items-center gap-3">
                <span className="w-6 text-right text-xs text-muted-foreground tabular-nums">{i + 1}</span>
                <Link to={`${creatorBasePath}/${c.id}`} className="flex-1 min-w-0 text-foreground hover:text-primary transition-colors truncate">
                  {c.display_name || c.tiktok_username || '—'}
                </Link>
                <span className="text-xs text-muted-foreground">{c.server || '—'}</span>
                <span className="text-sm font-semibold tabular-nums w-12 text-right">{fmtNum(c.valid_days)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
};

export default PerformanceAnalytics;
